// src/components/CapoOfficina/LavorazioniAutocomplete.jsx
import React, { useState, useEffect, useRef } from 'react';
import './LavorazioniAutocomplete.css';

const LavorazioniAutocomplete = ({
  lavorazioni = [],
  value = '',
  onChange,
  onSelect,
  onCreateNew,
  placeholder = "🔍 Cerca lavorazione...",
  disabled = false
}) => {
  const [searchTerm, setSearchTerm] = useState(value);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredLavorazioni, setFilteredLavorazioni] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showCreateButton, setShowCreateButton] = useState(false);
  
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Chiudi suggerimenti quando clicchi fuori
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtra lavorazioni quando cambia il termine di ricerca
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredLavorazioni([]);
      setShowCreateButton(false);
      return;
    }

    const search = searchTerm.toLowerCase().trim();
    const filtered = lavorazioni.filter(lav => 
      lav.nome?.toLowerCase().includes(search) ||
      lav.nomeLavorazione?.toLowerCase().includes(search) ||
      lav.descrizione?.toLowerCase().includes(search)
    );

    setFilteredLavorazioni(filtered);
    setHighlightedIndex(-1);
    
    // Mostra bottone "Crea nuova" se non ci sono risultati esatti
    const hasExactMatch = filtered.some(lav => 
      (lav.nome?.toLowerCase() === search) || 
      (lav.nomeLavorazione?.toLowerCase() === search)
    );
    setShowCreateButton(!hasExactMatch && searchTerm.length >= 3);
  }, [searchTerm, lavorazioni]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowSuggestions(true);
    if (onChange) onChange(value);
  };

  const handleSelectLavorazione = (lavorazione) => {
    const nome = lavorazione.nome || lavorazione.nomeLavorazione || '';
    setSearchTerm(nome);
    setShowSuggestions(false);
    if (onSelect) onSelect(lavorazione);
  };

  const handleCreateNew = () => {
    if (!searchTerm.trim()) return;
    
    if (onCreateNew) {
      onCreateNew(searchTerm.trim());
    }
    
    setSearchTerm('');
    setShowSuggestions(false);
    setShowCreateButton(false);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || (filteredLavorazioni.length === 0 && !showCreateButton)) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const maxIndex = showCreateButton ? filteredLavorazioni.length : filteredLavorazioni.length - 1;
        setHighlightedIndex(prev => 
          prev < maxIndex ? prev + 1 : prev
        );
        break;

      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;

      case 'Enter':
        e.preventDefault();
        if (highlightedIndex === -1) {
          // Nessuna selezione, cerca primo risultato
          if (filteredLavorazioni.length > 0) {
            handleSelectLavorazione(filteredLavorazioni[0]);
          } else if (showCreateButton) {
            handleCreateNew();
          }
        } else if (highlightedIndex === filteredLavorazioni.length && showCreateButton) {
          // Selezionato "Crea nuova"
          handleCreateNew();
        } else if (highlightedIndex < filteredLavorazioni.length) {
          // Selezionato un risultato
          handleSelectLavorazione(filteredLavorazioni[highlightedIndex]);
        }
        break;

      case 'Escape':
        e.preventDefault();
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;

      default:
        break;
    }
  };

  const highlightMatch = (text, search) => {
    if (!search || !text) return text;
    
    const parts = text.split(new RegExp(`(${search})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === search.toLowerCase() 
        ? <strong key={index} className="highlight">{part}</strong>
        : part
    );
  };

  return (
    <div className="lavorazioni-autocomplete" ref={wrapperRef}>
      <div className="input-wrapper">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="autocomplete-input"
          autoComplete="off"
        />
        
        {searchTerm && (
          <button
            type="button"
            className="clear-button"
            onClick={() => {
              setSearchTerm('');
              setShowSuggestions(false);
              if (onChange) onChange('');
              inputRef.current?.focus();
            }}
            title="Cancella ricerca"
          >
            ✕
          </button>
        )}
      </div>

      {showSuggestions && (filteredLavorazioni.length > 0 || showCreateButton) && (
        <div className="suggestions-dropdown">
          {/* Risultati trovati */}
          {filteredLavorazioni.map((lavorazione, index) => (
            <div
              key={lavorazione.id || index}
              className={`suggestion-item ${highlightedIndex === index ? 'highlighted' : ''}`}
              onClick={() => handleSelectLavorazione(lavorazione)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <div className="suggestion-main">
                <span className="suggestion-icon">🔧</span>
                <div className="suggestion-content">
                  <div className="suggestion-title">
                    {highlightMatch(
                      lavorazione.nome || lavorazione.nomeLavorazione || 'Lavorazione',
                      searchTerm
                    )}
                  </div>
                  {lavorazione.descrizione && (
                    <div className="suggestion-description">
                      {lavorazione.descrizione}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Bottone "Crea nuova lavorazione" */}
          {showCreateButton && (
            <>
              {filteredLavorazioni.length > 0 && (
                <div className="suggestions-divider">
                  <span>Oppure</span>
                </div>
              )}
              
              <div
                className={`suggestion-item create-new ${
                  highlightedIndex === filteredLavorazioni.length ? 'highlighted' : ''
                }`}
                onClick={handleCreateNew}
                onMouseEnter={() => setHighlightedIndex(filteredLavorazioni.length)}
              >
                <div className="suggestion-main">
                  <span className="suggestion-icon create-icon">➕</span>
                  <div className="suggestion-content">
                    <div className="suggestion-title create-title">
                      Crea nuova lavorazione: <strong>"{searchTerm}"</strong>
                    </div>
                    <div className="suggestion-description">
                      Verrà aggiunta al database per usi futuri
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Nessun risultato */}
          {filteredLavorazioni.length === 0 && !showCreateButton && (
            <div className="no-results">
              <span className="no-results-icon">🔍</span>
              <div className="no-results-text">
                <div>Nessuna lavorazione trovata</div>
                <div className="no-results-hint">
                  Scrivi almeno 3 caratteri per creare una nuova lavorazione
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Info box quando non ci sono risultati ma il termine è troppo corto */}
      {showSuggestions && searchTerm.length > 0 && searchTerm.length < 3 && filteredLavorazioni.length === 0 && (
        <div className="suggestions-dropdown">
          <div className="info-message">
            <span className="info-icon">💡</span>
            Scrivi almeno 3 caratteri per cercare o creare una lavorazione
          </div>
        </div>
      )}

      {/* Help text */}
      <div className="autocomplete-help">
      </div>
    </div>
  );
};

export default LavorazioniAutocomplete;